import { useMemo } from "react";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import type { Column } from "react-table";

import Badge from "components/v2/Badge/Badge";
import { ReactComponent as PackageUpIcon } from "assets/images/iconsV2/package-up.svg";
import { ReactComponent as PackageCheckIcon } from "assets/images/iconsV2/package-check.svg";
import { ReactComponent as PlaneIcon } from "assets/images/iconsV2/plane.svg";
import { ReactComponent as CargoShipIcon } from "assets/images/iconsV2/cargo-ship.svg";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { StopType, StopAddressType } from "types/MultiStop";
import {
  formatHoursTime,
  formatStopContactSummary,
  formatStopDate,
  getStopPortLabel,
} from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/reviewScheduleFormatters";

function ReviewScheduleAddressCell({ stop }: { stop: ScheduleStopValue }) {
  const intl = useIntl();
  const isPickup = stop.stop_type === StopType.PICKUP;
  const isAirport = stop.stop_address_type === StopAddressType.AIRPORT;
  const isSeaport = stop.stop_address_type === StopAddressType.SEAPORT;

  const portLabel = getStopPortLabel(stop);
  const streetLine = [stop.street_address, stop.street_address_line_two].filter(Boolean).join(", ");
  const regionLine = [stop.city_name, stop.state_name, stop.country_name, stop.zipcode].filter(Boolean).join(", ");
  const hasHours = !!(stop.stop_hours_start && stop.stop_hours_end);
  const contactSummary = formatStopContactSummary(stop);

  return (
    <div className="rfv2-review-schedule-table__address">
      <div className="rfv2-review-schedule-table__badge">
        <Badge
          label={
            isPickup
              ? intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" })
              : intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" })
          }
          color={isPickup ? "indigo" : "success"}
          type="category-badge"
          variant="icon-leading"
          size="sm"
          icon={isPickup ? <PackageUpIcon /> : <PackageCheckIcon />}
        />
      </div>

      {(isAirport || isSeaport || stop.address_name || streetLine || regionLine) && (
        <div className="rfv2-review-schedule-table__address-lines">
          {(isAirport || isSeaport) && portLabel && (
            <div className="rfv2-review__stop-address-type">
              <Badge
                label={
                  isAirport
                    ? intl.formatMessage({ id: "request-form.review.schedule.airport", defaultMessage: "Airport" })
                    : intl.formatMessage({ id: "request-form.review.schedule.port", defaultMessage: "Port" })
                }
                color="gray"
                type="status-badge"
                variant="icon-leading"
                size="sm"
                icon={isAirport ? <PlaneIcon width={16} height={16} /> : <CargoShipIcon width={16} height={16} />}
              />
              <span className="rfv2-review__field-value">{portLabel}</span>
            </div>
          )}

          {stop.address_name && <span className="rfv2-review__field-value">{stop.address_name}</span>}
          {streetLine && <span className="rfv2-review__field-value">{streetLine}</span>}
          {regionLine && <span className="rfv2-review__field-value">{regionLine}</span>}
        </div>
      )}

      {hasHours && (
        <span className="rfv2-review-schedule-table__hours">
          {intl.formatMessage(
            { id: "request-form.review.schedule.hours", defaultMessage: "Hours: {start} - {end}" },
            {
              start: formatHoursTime(stop.stop_hours_start as string),
              end: formatHoursTime(stop.stop_hours_end as string),
            },
          )}
        </span>
      )}

      {contactSummary && <span className="rfv2-review-schedule-table__contact">{contactSummary}</span>}
    </div>
  );
}

export function useReviewScheduleColumns(
  intl: IntlShape,
  getTimezoneLabel: (value?: string | null) => string,
): Column<ScheduleStopValue>[] {
  return useMemo(
    () => [
      {
        id: "address",
        Header: intl.formatMessage({
          id: "request-form.review.schedule.table.address",
          defaultMessage: "Address",
        }),
        width: 200,
        Cell: ({ row }: { row: { original: ScheduleStopValue } }) => <ReviewScheduleAddressCell stop={row.original} />,
      },
      {
        id: "requestedTime",
        Header: intl.formatMessage({
          id: "request-form.review.schedule.table.requestedTime",
          defaultMessage: "Requested time",
        }),
        width: 100,
        Cell: ({ row }: { row: { original: ScheduleStopValue } }) => (
          <span>{formatStopDate(row.original, intl, getTimezoneLabel) || "-"}</span>
        ),
      },
      {
        id: "instructions",
        Header: intl.formatMessage({
          id: "request-form.review.schedule.table.instructions",
          defaultMessage: "Instructions",
        }),
        Cell: ({ row }: { row: { original: ScheduleStopValue } }) => (
          <span className="rfv2-review__field-value">{row.original.stop_notes || "-"}</span>
        ),
      },
    ],
    [intl, getTimezoneLabel],
  );
}
